import axios from '../../config/axiosConfig';
import { all, call, cancelled, fork, put, select, takeLatest } from 'redux-saga/effects';
import {
  loadItemPerformanceDataFailure,
  loadItemPerformanceDataSuccess,
  loadItemResponseFailure,
  loadItemResponseSuccess,
  loadItemSampleResponsesFailure,
  loadItemSampleResponsesSuccess,
  loadItemScoreGuideFailure,
  loadItemScoreGuideSuccess
} from './itemDetail.actions';
import { IItemRequest, PDOutputType } from '../../shared';
import { selectPDSelections, selectPDSelectors } from '../explorePanel/explorePanel.selectors';
import { CHART_SELECTIONS, ExplorePerformanceDataSelectorsResponse, PerformanceDataSelections } from '../explorePanel/models';
import { selectItemRequest } from '../queryResults/queryResults.selectors';
import { GridItem, ItemRequestPayload } from '../queryResults/models';
import { selectItem } from './itemDetail.selectors';
import { IItemResponse, ItemDetailActionTypes, ITEM_DETAIL_ACTIONS, PerformanceDataResponse } from './models';

// TODO: PLUG IN loadItemPerformanceDataAsync
/**
 * Root ItemDetail Saga.
 * Dispatches loadItemResponseAsync, loadItemScoreGuideAsync, loadItemSampleResponsesAsync
 * Note: loadItemPerformanceData is not dispatched since we need the default jurisdiction and
 * variable selector Ids
 */
export function* loadItemDetailAsync({payload}: {payload: ItemRequestPayload; type: ITEM_DETAIL_ACTIONS}): Generator {
  yield fork(loadItemResponseAsync, payload.gridItem);
  yield fork(loadItemScoreGuideAsync, payload.gridItem);
  yield fork(loadItemSampleResponsesAsync, payload.gridItem);
}

/**
 * Fetches itemResponse
 * then dispatches an action with the corresponding result
 */
export function* loadItemResponseAsync(payload: GridItem): Generator {
  const controller = new AbortController();
  const signal = controller.signal;
  try {
    const params = {
      tableID: payload.itemTableIDAsInt,
    };
    const response = yield axios.get<IItemResponse>('api/queryresults/GetItem', {params, signal})
      .then((res) => res.data);
    yield put(loadItemResponseSuccess(<IItemResponse>response));
  } catch (error) {
    yield put(loadItemResponseFailure(error));
  } finally {
    // if cancelled, abort the request
    if (yield cancelled()) {
      controller.abort();
    }
  }
}

/**
 * Fetches itemScoreGuide
 * then dispatches an action with the corresponding result
 */
export function* loadItemScoreGuideAsync(payload: GridItem): Generator {
  const controller = new AbortController();
  const signal = controller.signal;
  try {
    const params = {
      tableID: payload.itemTableIDAsInt
    };

    const response = yield axios.get<string>('api/queryresults/GetItemScoreGuide', {params, signal})
      .then((res) => res.data );
    yield put(loadItemScoreGuideSuccess(<string>response));
  } catch (error) {
    yield put(loadItemScoreGuideFailure(error));
  } finally {
    // if cancelled, abort the request
    if (yield cancelled()) {
      controller.abort();
    }
  }
}

/**
 * Fetches itemSampleResponses
 * then dispatches an action with the corresponding result
 */
export function* loadItemSampleResponsesAsync(payload: GridItem): Generator {
  // No SampleResponse if question itemType is multiple choice
  // if (payload.type === 'MC') {
  //   yield put(loadItemSampleResponsesSuccess(''));
  //   return;
  // }
  const controller = new AbortController();
  const signal = controller.signal;
  try {
    const params = {
      tableID: payload.itemTableIDAsInt,
    };
    const response = yield axios.get<string>('api/queryresults/GetItemSampleResponses', {params, signal})
      .then((res) => res.data );
      
    yield put(loadItemSampleResponsesSuccess(<string>response));
  } catch (error) {
    yield put(loadItemSampleResponsesFailure(error));
  } finally {
    // if cancelled, abort the request
    if (yield cancelled()) {
      controller.abort();
    }
  }
}

/**
 * Triggered by loadItemPerformanceDataStart action
 * Fetches itemPerformanceData
 * then dispatches an action with the corresponding result
 */
export function* loadItemPerformanceDataAsync(): Generator {
  const controller = new AbortController();
  const signal = controller.signal;
  try {
    const selectCurrentItem = yield select(selectItem);
    const selectCurrentRequest = yield select(selectItemRequest);
    const selectSelectors = yield select(selectPDSelectors);
    const selectPD = yield select(selectPDSelections);
    const item = selectCurrentItem as GridItem;
    const itemRequest = selectCurrentRequest as IItemRequest;
    const pdSelectors = selectSelectors as ExplorePerformanceDataSelectorsResponse;
    const pdSelections = selectPD as PerformanceDataSelections;
    let body;
    if (pdSelections.output !== PDOutputType.OUTPUT_CHART) {
      // setup default options
      body = {
        itemTableID: item.itemTableIDAsInt,
        ndeSystemId: itemRequest.SystemID,
        subjectCode: itemRequest.SubjectCode,
        jurisdictionsSelected: pdSelections.jurisdictionsSelected,
        output: pdSelections.output,
        showStandardError: pdSelections.showStandardError,
        statistics: pdSelections.statistics,
        variablesSelected: pdSelections.variablesSelected
      };
    } else {
      body = {
        itemTableID: item.itemTableIDAsInt,
        ndeSystemId: itemRequest.SystemID,
        subjectCode: itemRequest.SubjectCode,
        ...CHART_SELECTIONS,
        jurisdictionsSelected: pdSelectors.defaultJurisID,
        variablesSelected: pdSelections.variablesSelected
      };
    }

    const response = yield axios.post<PerformanceDataResponse>('api/queryresults/GetItemPerformanceData', {...body}, {signal})
      .then((res) => res.data);
    yield put(loadItemPerformanceDataSuccess(<PerformanceDataResponse>response));
  } catch (error) {
    yield put(loadItemPerformanceDataFailure(error));
  } finally {
    // if cancelled, abort the request
    if (yield cancelled()) {
      controller.abort();
    }
  }
}

/**
 * Listens for LOAD_ITEM_START action, calls root saga loadItemDetailAsync, which calls multiple sagas:
 * loadItemResponseAsync,
 * loadItemScoreGuideAsync,
 * loadItemSampleResponsesAsync,
 */
export function* loadItemDetailStartSaga(): Generator {
  yield takeLatest(ItemDetailActionTypes.LOAD_ITEM_START, loadItemDetailAsync);
}

/**
 * Listens for LOAD_PERFORMANCE_DATA_START, calls loadItemPerformanceDataAsync.
 */
export function* loadItemPerformanceDataStartSaga(): Generator {
  yield takeLatest(ItemDetailActionTypes.LOAD_PERFORMANCE_DATA_START, loadItemPerformanceDataAsync);
}

/**
 * Composite of all itemDetail sagas that are listening for external actions
 */
export function* itemDetailSagas(): Generator {
  yield all([call(loadItemDetailStartSaga), call(loadItemPerformanceDataStartSaga)]);
}

export default itemDetailSagas;